import { ref, watch, computed, type ComputedRef, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { BaseIconName, DropdownItemType } from '@cplug/design-system/components/props'
import type { DropdownItem } from '@cplug/design-system/components/props'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import type { User } from '@cplug/frontend-shared-domains/domain/user'
import { useWhitelabelStore } from '@cplug/frontend-shared-domains/domain/whitelabel'
import { useRedirectToSubscriptionHub } from '~/domain/shared/navigation/composables/useRedirectToSubscriptionHub'
import { useTranslation } from '~/domain/shared/navigation/components/BaseHeaderRightSide.i18n'
import { finalizeLogoutSessionState } from '~/domain/shared/authentication/composables/useSessionRestoreState'
import { useBrandStore } from '~/domain/shared/branding/stores/useBrandStore'
import useLoggedUserStore from '~/domain/dashboard/user/logged-user/stores/useLoggedUserStore'
import type { LoggedUser } from '~/domain/dashboard/user/logged-user/models/LoggedUser'

// BaseDropdownActionList is a generic component, so `InstanceType<typeof ...>` cannot be resolved here.
interface DropdownActionListInstance {
  hide: () => void
  show: (event?: Event) => Promise<void>
  toggle: (event?: Event) => void
}

interface SidebarUserMenuComposable {
  loggedUser: Ref<LoggedUser | undefined>
  onClickUser: (event: MouseEvent) => void
  onUserMenuClick: (item: DropdownItem<() => void>) => void
  t: ReturnType<typeof useTranslation>['t']
  user: ComputedRef<User | undefined>
  userMenu: Ref<DropdownActionListInstance | undefined>
  userMenuItems: ComputedRef<DropdownItem<() => void>[]>
  versionInfoDialogOpen: Ref<boolean>
}

export function useSidebarUserMenu (): SidebarUserMenuComposable {
  const { t } = useTranslation()
  const authStore = useAuthenticationStore()
  const { companies: selectedCompanies } = storeToRefs(authStore)
  const user = computed(() => authStore.user)
  const router = useRouter()

  const whitelabelStore = useWhitelabelStore()
  const { isPartner, isWhitelabel } = storeToRefs(whitelabelStore)
  const brandStore = useBrandStore()
  const { isPartnerBrand } = storeToRefs(brandStore)

  const loggedUserStore = useLoggedUserStore()
  loggedUserStore.get()
  const { model: loggedUser } = storeToRefs(loggedUserStore)

  const userMenu = ref<DropdownActionListInstance>()
  const versionInfoDialogOpen = ref(false)
  const selectedCompany = computed(() => selectedCompanies.value[0])

  function openVersionInfo (): void {
    versionInfoDialogOpen.value = true
  }

  function redirectMyProfile (): void {
    router.push({ path: '/dashboard/profile' })
  }

  function redirectMyCompany (): void {
    if (!selectedCompany.value?.id) {
      return
    }
    router.push({ path: `/dashboard/companies/edit/${selectedCompany.value.id}` })
  }

  const userMenuItems = computed<DropdownItem<() => void>[]>(() => [
    {
      key: 'profile',
      label: t('MyProfile'),
      leadIcon: BaseIconName.User,
      value: redirectMyProfile
    },
    ...(!isPartner.value && !isWhitelabel.value && !isPartnerBrand.value
      ? [{
          key: 'my-plan',
          label: t('MyPlan'),
          leadIcon: BaseIconName.CreditCard,
          value: (): void => { useRedirectToSubscriptionHub() }
        }]
      : []),
    {
      key: 'my-company',
      label: t('MyCompany'),
      leadIcon: BaseIconName.Building,
      value: redirectMyCompany
    },
    {
      key: 'system-info',
      label: t('SystemInfo'),
      leadIcon: BaseIconName.InfoCircle,
      value: openVersionInfo
    },
    {
      key: 'logout-divisor',
      label: '',
      type: DropdownItemType.Divisor,
      value: (): void => {}
    },
    {
      key: 'logout',
      label: t('Logout'),
      leadIcon: BaseIconName.SignOut,
      value: async (): Promise<void> => {
        await authStore.logout()
        finalizeLogoutSessionState()
      }
    }
  ])

  function onUserMenuClick (item: DropdownItem<() => void>): void {
    item.value?.()
  }

  function onClickUser (event: MouseEvent): void {
    userMenu.value?.toggle(event)
  }

  watch(
    () => loggedUser.value,
    () => {
      authStore.setUser(loggedUser.value)
    }
  )

  return {
    loggedUser,
    onClickUser,
    onUserMenuClick,
    t,
    user,
    userMenu,
    userMenuItems,
    versionInfoDialogOpen
  }
}
